export const getCertificateReferenceId = (cert: any) => {
  if (!cert?.reference) return ''
  if (typeof cert.reference === 'string') return String(cert.reference)
  return String(cert.reference._id ?? cert.reference.id ?? '')
}

export const getCertificatePreviewUrl = (cert: any) => {
  if (!cert) return ''
  if (typeof cert.document === 'string') return cert.document
  return cert.document?.url ?? cert.image ?? ''
}

export const getCertificateTitle = (cert: any, fallback = '-') => {
  if (typeof cert?.reference === 'object' && cert.reference?.title) {
    return cert.reference.title
  }
  return cert?.title ?? fallback
}

export const hasUploadedCertificateDocument = (cert: any) =>
  Boolean(getCertificatePreviewUrl(cert))

const getSelectedCategoryIds = (categories: any[], categoryMaster: any[] = []) => {
  const ids = new Set<string>()
  categories.forEach((selectedCat: any) => {
    const refId = selectedCat.reference?._id ?? selectedCat._id
    if (refId) ids.add(String(refId))
    const masterCat = categoryMaster.find((c: any) => String(c._id) === String(refId))
    if (masterCat?.uTradeCategoryId) ids.add(String(masterCat.uTradeCategoryId))
    const uTradeId = selectedCat.reference?.uTradeCategoryId ?? selectedCat.uTradeCategoryId
    if (uTradeId) ids.add(String(uTradeId))
  })
  return ids
}

/** Required certificate master records for the business's selected categories. */
export const getRequiredCertificateRecords = (
  certificateRecords: any[] = [],
  categories: any[] = [],
  categoryMaster: any[] = []
) => {
  if (!certificateRecords.length) return []
  const selectedCategoryIds = getSelectedCategoryIds(categories, categoryMaster)
  if (!selectedCategoryIds.size) return certificateRecords
  return certificateRecords.filter((cert: any) =>
    cert.categories?.some((cat: any) => selectedCategoryIds.has(String(cat._id)))
  )
}

/**
 * Stable UI order: follow master certificate list order, then any uploaded
 * certs that are not in that list. Pending slots stay in place instead of
 * jumping when a file is uploaded.
 */
export const buildOrderedCertificateSlots = ({
  uploadedCertificates = [],
  pendingCertificates = [],
  certificateRecords = [],
  categories = [],
  categoryMaster = [],
}: {
  uploadedCertificates?: any[]
  pendingCertificates?: any[]
  certificateRecords?: any[]
  categories?: any[]
  categoryMaster?: any[]
}) => {
  const uploadedById = new Map<string, any>()
  uploadedCertificates.forEach((cert) => {
    const id = getCertificateReferenceId(cert)
    if (id) uploadedById.set(id, cert)
  })

  const pendingById = new Map<string, any>()
  pendingCertificates.forEach((cert) => {
    const id = String(cert?._id ?? '')
    if (id) pendingById.set(id, cert)
  })

  const requiredRecords = getRequiredCertificateRecords(
    certificateRecords,
    categories,
    categoryMaster
  )
  const orderedIds = new Set<string>()
  const slots: Array<{ kind: 'uploaded' | 'pending'; id: string; cert: any }> = []

  const masterList =
    requiredRecords.length > 0
      ? requiredRecords
      : [
          ...uploadedCertificates
            .map((cert) => cert?.reference)
            .filter((ref) => ref && typeof ref === 'object'),
          ...pendingCertificates,
        ]

  masterList.forEach((record: any) => {
    const id = String(record?._id ?? record?.id ?? '')
    if (!id || orderedIds.has(id)) return
    orderedIds.add(id)

    const uploaded = uploadedById.get(id)
    if (uploaded) {
      slots.push({ kind: 'uploaded', id, cert: uploaded })
      return
    }

    const pending = pendingById.get(id) ?? record
    slots.push({ kind: 'pending', id, cert: pending })
  })

  // Keep any leftover uploaded certs (no longer required) after the master order
  uploadedCertificates.forEach((cert) => {
    const id = getCertificateReferenceId(cert)
    if (!id || orderedIds.has(id)) return
    orderedIds.add(id)
    slots.push({ kind: 'uploaded', id, cert })
  })

  pendingCertificates.forEach((cert) => {
    const id = String(cert?._id ?? '')
    if (!id || orderedIds.has(id)) return
    orderedIds.add(id)
    slots.push({ kind: 'pending', id, cert })
  })

  return slots
}
